import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AgentAmorphicService } from '../amorphic/amorphic.service';

import { Controller } from '../../Controller';
import { CaseCounts, AccountCenterListRecordView } from '../../types/Types';
import { ProcessPostService } from './process-post.service';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

@Injectable({ providedIn: 'root' })
export class AccountCenterCachingService {
  private controller: Controller;
  private cacheStartTime = new Date().getTime();
  private CACHE_TTL = 2 * 60 * 1000; // in milliseconds

  constructor(amorphicService: AgentAmorphicService, protected processPostService: ProcessPostService) {
    this.controller = amorphicService.controller as Controller;
  }

  public triggerPreFetch() {
    if (this.controller.listViewCachingEnabled && this.controller.accountCenterController.shouldPreFetch) {
      this.controller.accountCenterController.shouldPreFetch = false;
      this.preFetchAccountCenterData();
    }
  }

  private preFetchConfig = {
    fetchListViewRecordsPaginated: {
      pageNumberStart: 0,
      recordsPerPage: 20,
    },
  };
  usePreFetch = { totalCount: false, cases: false, counts: false };
  preFetch = { totalCountPromise: null, casesPromise: null, countsPromise: null };
  private preFetchAccountCenterData(): void {
    this.usePreFetch = { totalCount: true, cases: true, counts: true };
    this.preFetch.totalCountPromise = this.processPostService
      .fetchListViewCaseCount()
      .catch((e) => this.logAndIgnore(e));
    const { pageNumberStart, recordsPerPage } = this.preFetchConfig.fetchListViewRecordsPaginated;
    this.preFetch.casesPromise = this.processPostService
      .fetchRecordsPaginated(pageNumberStart, recordsPerPage)
      .catch((e) => this.logAndIgnore(e));

    this.preFetch.countsPromise = this.processPostService.fetchCaseCounts().catch((e) => this.logAndIgnore(e));
  }

  private logAndIgnore(e: Error) {
    this.invalidateCache();
    const err = {
      isHumanRelated: false,
      name: e.name,
      message: e.message,
      stack: e.stack,
    };
    UILoggerSingleton.getInstance().error(
      {
        module: 'CustomerController',
        function: 'updateEmailOnServer',
        category: UICategory.request,
        error: err,
      },
      'PreFetch failed'
    );
  }

  updateCache(cacheData: { totalCount: number; cases: Array<AccountCenterListRecordView>; counts: CaseCounts }): void {
    /**
     * @TODO HL-54523 -- Enable caching for Tele-Interviewer cases
     */
    if (this.controller.isTeleInterviewerLoggedIn) {
      return;
    }

    const cache = this.controller.accountCenterController.cache;
    if (cacheData.totalCount) {
      cache.setTotalCount(cacheData.totalCount);
    }
    if (cacheData.cases) {
      cache.setCases(cacheData.cases);
    }
    if (cacheData.counts) {
      cache.setCounts(cacheData.counts);
    }
  }

  readCache(key: 'totalCount' | 'cases' | 'counts'): number | Array<AccountCenterListRecordView> | CaseCounts {
    const cache = this.controller.accountCenterController.cache;
    if (key === 'totalCount') {
      return cache.totalCount;
    }
    if (key === 'cases') {
      return cache.getCases();
    }
    if (key === 'counts') {
      return cache.getCounts();
    }
  }
  public resetTTL() {
    this.cacheStartTime = new Date().getTime();
  }
  private isWithinTTL() {
    const now = new Date().getTime();
    const ttl = now - this.cacheStartTime;
    const isWithinTTL = ttl < this.CACHE_TTL;
    return isWithinTTL;
  }

  public isAllCached(): boolean {
    if (!this.controller.listViewCachingEnabled) {
      return false;
    }

    const isWithinTTL = this.isWithinTTL();

    const cache = this.controller.accountCenterController.cache;
    const isAllValid = cache.isAllValid();

    return isWithinTTL && isAllValid;
  }

  invalidateCache() {
    this.usePreFetch = { totalCount: false, cases: false, counts: false };
    const cache = this.controller.accountCenterController.cache;
    cache.invalidateAll();
  }
  tryInvalidateCacheByStatus(
    applicationNumber: string,
    status: string,
    subStatus,
    progressPercentage,
    insuredNameDisplay,
    ownerNameDisplay,
    otherOwnerName
  ) {
    const cache = this.controller.accountCenterController.cache;
    if (
      cache.shouldInvalidateByStatus({
        applicationNumber,
        status,
        subStatus,
        progressPercentage,
        insuredNameDisplay,
        ownerNameDisplay,
        otherOwnerName,
      })
    ) {
      this.invalidateCache();
    }
  }
}
